import { useEffect, useRef, useState } from 'react'
import { Html } from '@react-three/drei'
import { track } from '@vercel/analytics'
import useGame from '../stores/useGame.jsx'
import * as THREE from 'three'
import { loadSECData, getSECDataById } from '../utils/secDataLoader.js'

// ---------------------------------------------------------------------------
// Sub-section config — single source of truth.
// Key: the SEC ID that is a sub-section.
// Value: { parent } — the SEC that must be active to reveal this marker.
// ---------------------------------------------------------------------------
const SEC_SUBSECTIONS = {
    SEC006: { parent: 'SEC001' }
}

// SEC IDs that show the highlight animation (pulse + glint) in default view.
const HIGHLIGHTED_SECS = new Set(['SEC001', 'SEC006'])

export function SEC_Markers({ nodes }) {
    const [secGroups, setSecGroups] = useState([])
    const [secDataMap, setSecDataMap] = useState(null)
    const [posNodes, setPosNodes] = useState({})
    const [tarNodes, setTarNodes] = useState({})
    const secNodesRef = useRef({})
    const [hoveredIndex, setHoveredIndex] = useState(null)
    const [openMarkerIndex, setOpenMarkerIndex] = useState(null)
    const [fadeInComplete, setFadeInComplete] = useState(false)
    // Tracks whether sub-section markers should be visible (faded in or out)
    const [subSectionVisible, setSubSectionVisible] = useState(false)
    // Ref tracking the previous open index so the side-effect useEffect below
    // can dispatch the deselect event without being inside a setState updater.
    const prevOpenMarkerIndexRef = useRef(null)
    
    // Get state from store
    const selectedSec = useGame((state) => state.selectedSec)
    const viewState = useGame((state) => state.viewState)
    const enterSecZoom = useGame((state) => state.enterSecZoom)
    const exitToDefault = useGame((state) => state.exitToDefault)
    const cinematicMode = useGame((state) => state.cinematicMode)
    const openPoiIndex = useGame((state) => state.openPoiIndex)
    const deviceType = useGame((state) => state.deviceType)

    // Load SEC data from CSV on mount
    useEffect(() => {
        loadSECData().then(data => {
            setSecDataMap(data)
        })
    }, [])

    // Initialize SEC groups and camera position nodes on mount - detect from GLB nodes
    useEffect(() => {
        if (!nodes) {
            return
        }

        const secGroupsList = []
        const posNodesMap = {}
        const tarNodesMap = {}
        
        // Scan all nodes for SEC, POS, and TAR nodes
        Object.entries(nodes).forEach(([nodeName, node]) => {
            const upperName = nodeName.toUpperCase()
            
            // Handle SEC nodes (markers)
            if (nodeName.includes('SEC') || upperName.includes('SEC')) {
                if (!upperName.includes('POS') && !upperName.includes('TAR')) {
                    // Store the node and calculate its world position
                    secNodesRef.current[nodeName] = node
                    
                    const worldPosition = new THREE.Vector3()
                    
                    // Get the world position of the node
                    if (node.position) {
                        worldPosition.copy(node.position)
                    }
                    
                    // If it's a mesh with geometry, use its center
                    if (node.isMesh && node.geometry) {
                        node.geometry.computeBoundingBox()
                        const center = new THREE.Vector3()
                        node.geometry.boundingBox.getCenter(center)
                        worldPosition.add(center)
                    }
                    
                    secGroupsList.push({
                        name: nodeName,
                        node: node,
                        worldPosition: worldPosition,
                        isDefault: false
                    })
                }
            }
            
            // Handle POS nodes (camera positions)
            if (nodeName.includes('POS') || upperName.includes('POS')) {
                posNodesMap[nodeName] = node
            }
            
            // Handle TAR nodes (camera targets)
            if (nodeName.includes('TAR') || upperName.includes('TAR')) {
                tarNodesMap[nodeName] = node
            }
        })
                
        setSecGroups(secGroupsList)
        setPosNodes(posNodesMap)
        setTarNodes(tarNodesMap)
        
        // Removed verbose discovery logs
    }, [nodes])

    // Ensure marker toggle resets when a SEC is deselected (back or switching)
    useEffect(() => {
        const handleSecDeselected = () => {
            // Clear local open state so next click immediately re-enters zoom
            setOpenMarkerIndex(null)
        }
        window.addEventListener('sec-deselected', handleSecDeselected)
        return () => window.removeEventListener('sec-deselected', handleSecDeselected)
    }, [])

    // Also clear if returning fully to default view (safety in case of missing event)
    useEffect(() => {
        if (viewState === 'default_state' && openMarkerIndex !== null) {
            setOpenMarkerIndex(null)
        }
    }, [viewState, openMarkerIndex])

    // React to openMarkerIndex changes: dispatch window events and enter SEC zoom.
    // This replaces the anti-pattern of doing side effects inside a setState updater.
    useEffect(() => {
        const index = openMarkerIndex
        const prevIndex = prevOpenMarkerIndexRef.current

        if (index === null) {
            prevOpenMarkerIndexRef.current = null
            return
        }

        const sec = secGroups[index]
        const secData = secDataMap ? getSECDataById(secDataMap, sec?.name) : null

        // If we switched from another marker, emit deselect for the old one first
        if (prevIndex !== null && prevIndex !== index) {
            window.dispatchEvent(new CustomEvent('sec-deselected', {
                detail: {
                    secName: secGroups[prevIndex]?.name,
                    index: prevIndex,
                    toSecName: sec?.name
                }
            }))
        }

        prevOpenMarkerIndexRef.current = index

        // Enter zoom for the newly selected marker
        enterSecZoom({
            secId: secData?.id || sec?.name,
            posId: secData?.position || null,
            tarId: secData?.target || null,
        })
        window.dispatchEvent(new CustomEvent('sec-selected', {
            detail: {
                secName: sec?.name,
                secData: secData,
                index: index
            }
        }))
    }, [openMarkerIndex]) // intentionally omits secGroups/secDataMap — they are stable after load
    
    // Initial fade-in animation for SEC markers - triggered after model/nodes are loaded
    useEffect(() => {
        // Only trigger fade-in once nodes are available (model is loaded)
        if (!nodes || secGroups.length === 0) return
        
        // 1 second delay after model is loaded, then trigger fade-in
        const timer = setTimeout(() => {
            setFadeInComplete(true)
        }, 1000)
        return () => clearTimeout(timer)
    }, [nodes, secGroups.length])

    // Show/hide sub-section markers based on whether their parent SEC is active.
    // We keep the marker mounted during fade-out (via subSectionVisible staying true
    // briefly) so the CSS opacity transition has time to complete before unmounting.
    useEffect(() => {
        const parentIsActive = Object.values(SEC_SUBSECTIONS).some(
            cfg => cfg.parent === selectedSec
        ) || Object.keys(SEC_SUBSECTIONS).includes(selectedSec)

        if (parentIsActive) {
            setSubSectionVisible(true)
        } else {
            // Delay unmount to let the CSS fade-out finish (matches sec-hidden transition 0.3s)
            const timer = setTimeout(() => setSubSectionVisible(false), 350)
            return () => clearTimeout(timer)
        }
    }, [selectedSec])

    return (
        <>
            {secGroups.map((sec, index) => {
                const isHovered = index === hoveredIndex
                const isOpen = index === openMarkerIndex

                // Get SEC data from CSV for this specific SEC node
                const secData = secDataMap ? getSECDataById(secDataMap, sec.name) : null

                // Sub-section handling — only render if its parent SEC is active
                const subSecCfg = SEC_SUBSECTIONS[sec.name]
                if (subSecCfg && !subSectionVisible) return null

                // Marker is hidden when: zoomed into this SEC, leaving parent context,
                // a POI window is open, or cinematic mode is active
                const isLeavingParentContext = subSecCfg && !Object.values(SEC_SUBSECTIONS).some(
                    cfg => cfg.parent === selectedSec
                ) && selectedSec !== sec.name

                const isHidden = (viewState === 'sec_zoomed' && !subSecCfg)
                    || isLeavingParentContext
                    || (openPoiIndex !== null && !subSecCfg)
                    || (subSecCfg && selectedSec === (secData?.id || sec.name))

                // Sub-section markers are always solid; others solid in default state or when hovered
                const shouldBeSolid = (subSecCfg != null) || viewState === 'default_state' || isHovered

                // Highlight: top-level markers glow at default_state; sub-section markers
                // glow whenever they are visible (i.e. their parent SEC is active)
                const isHighlighted = HIGHLIGHTED_SECS.has(secData?.id)
                    && !isHidden
                    && (subSecCfg ? true : viewState === 'default_state')

                const markerClassName = [
                    'textButtonContainer',
                    'sec-marker-dynamic',
                    shouldBeSolid ? 'sec-solid' : 'sec-faded',
                    (isHidden || cinematicMode) ? 'sec-hidden' : '',
                    !fadeInComplete ? 'sec-initial-fade' : '',
                    isHighlighted ? 'sec-marker-highlighted' : '',
                ].filter(Boolean).join(' ')

                return (
                    <Html
                        zIndexRange={[0, 3500]}
                        wrapperClass="sec-html-portal"
                        key={`sec-${index}`}
                        position={sec.worldPosition}
                        occlude={false}
                        center
                    >
                        <div className={markerClassName}>
                            <div
                                className={`sec-marker-wrapper ${isOpen ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                                onPointerOver={(e) => { e.stopPropagation(); setHoveredIndex(index) }}
                                onPointerOut={(e) => { e.stopPropagation(); setHoveredIndex((h) => (h === index ? null : h)) }}
                                onClick={(e) => {
                                    e.stopPropagation()

                                    track('sec_marker_clicked', {
                                        secName: sec.name,
                                        section: secData?.section || 'Unknown',
                                        index: index
                                    })

                                    setOpenMarkerIndex(index)
                                }}
                                title={secData?.section || sec.name}
                            >
                                <span className="sec-label textButton">
                                    {(() => {
                                        const label = secData?.section || sec.name
                                        return deviceType === 'phone' ? label.replace(/ Systems$/i, '') : label
                                    })()}
                                </span>
                                <div className="textButtonArrow arrowButton sec-marker" />
                            </div>
                        </div>
                    </Html>
                )
            })}
        </>
    )
}
